import { Undef } from 'types/UtilityTypes';
import captureException from 'utils/captureException';

import { QuotingPortal } from './interface';

export const ShipperGuideSubmitAction = 'shipper-guide-submit-quote';
export class ShipperGuide implements QuotingPortal {
  submitAction = ShipperGuideSubmitAction;
  /**
   * Determines if a quote can be submitted to LoadSmart/ShipperGuide based on the current tab and document state.
   * @param tab The current Chrome tab
   * @param html The current document HTML
   * @returns boolean indicating if a quote can be submitted
   */
  canSubmit(tab: Undef<chrome.tabs.Tab>, html: string): boolean {
    if (!tab?.url) return false;

    try {
      const url = new URL(tab.url);
      if (!url.origin?.includes('loadsmart.com')) return false;

      const isBidPage =
        url.pathname.includes('/bids/') || url.hash.includes('/bids/');
      if (!isBidPage) return false;

      // On /single and /bulk pages the entire bid form (inputs + submit button)
      // lives inside web components whose shadow DOM is not serialized in the HTML
      // snapshot. Trust the URL alone for these pages.
      if (url.hash.includes('/single') || url.hash.includes('/bulk')) {
        return true;
      }

      // For other bid pages (e.g. inline bidding on the bids-list), detect the
      // form elements in the HTML snapshot.
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const hasLoadSmartFlatRateInput =
        doc.querySelector(
          'input[data-testid="flat-rate"], input[id="flat-rate"], input[name="flat-rate"]'
        ) !== null;
      const hasLoadSmartRatePerMileInput =
        doc.querySelector(
          'input[data-testid="rate-per-mile"], input[id="rate-per-mile"], input[name="rate-per-mile"]'
        ) !== null;
      const hasLoadSmartSubmitButton =
        doc.querySelector('m-button') !== null && html.includes('Submit');

      return (
        hasLoadSmartSubmitButton &&
        (hasLoadSmartFlatRateInput || hasLoadSmartRatePerMileInput)
      );
    } catch (error: any) {
      captureException(error, {
        context: 'Error checking LoadSmart/ShipperGuide quote page',
      });
      return false;
    }
  }
}
